import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
  LinearProgress,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { ShowChart as ShowChartIcon, WbSunny as WbSunnyIcon } from '@mui/icons-material';
import { statisticsApi, energyApi } from '../api/apiService';
import { EnergyResponse, CategoryEnergyStats, EnergyConfigEntry } from '../types/energy';
import { categories as energyCategories } from '../data/energyCategories';
import { getTotalStarsEarned, getCelestialCounts, getConfigForCategory } from '../utils/energyCalculations';

interface PointsStats {
  period: string;
  points: number;
}

interface StatisticsData {
  daily: PointsStats[];
  weekly: PointsStats[];
  monthly: PointsStats[];
}

const StatisticsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [statistics, setStatistics] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [energyStats, setEnergyStats] = useState<CategoryEnergyStats[]>([]);
  const [energyConfig, setEnergyConfig] = useState<EnergyConfigEntry[]>([]);
  const [energyLoading, setEnergyLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchStatistics();
      fetchEnergyStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await statisticsApi.getForUser(userId!);
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnergyStats = async () => {
    try {
      setEnergyLoading(true);
      const res = await energyApi.getUserEnergy(userId!);
      const data: EnergyResponse = res.data;
      setEnergyStats(data.stats || []);
      setEnergyConfig(data.config || []);
    } catch (err) {
      console.error('Failed to load energy stats:', err);
    } finally {
      setEnergyLoading(false);
    }
  };

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: 'daily' | 'weekly' | 'monthly' | null) => {
    if (newPeriod) {
      setPeriod(newPeriod);
    }
  };

  const formatPeriodLabel = (periodStr: string, type: 'daily' | 'weekly' | 'monthly'): string => {
    if (type === 'daily') {
      const date = new Date(periodStr);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } else if (type === 'weekly') {
      return periodStr.replace('W', ' W');
    } else {
      const [, month] = periodStr.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthNames[parseInt(month) - 1];
    }
  };

  const getChartData = (): PointsStats[] => {
    if (!statistics) return [];
    return statistics[period].map(item => ({
      ...item,
      label: formatPeriodLabel(item.period, period)
    }));
  };

  const getTotalPoints = (): number => {
    if (!statistics) return 0;
    return statistics[period].reduce((sum, item) => sum + item.points, 0);
  };

  const getBarColor = (entry: PointsStats, index: number): string => {
    if (entry.points > 0) {
      return '#6366f1';
    }
    return '#e2e8f0';
  };

  const getEnergyChartData = () => {
    return energyStats.map((s) => {
      const cfg = getConfigForCategory(energyConfig, s.category);
      const totalStars = cfg ? getTotalStarsEarned(s.totalEnergy, cfg.initialThreshold, cfg.growthRate) : 0;
      const cat = energyCategories.find((c) => c.key === s.category);
      return {
        name: cat?.label ?? s.category,
        icon: cat?.icon ?? '',
        energy: s.totalEnergy,
        stars: totalStars,
        color: cat?.color ?? '#999',
      };
    }).sort((a, b) => b.energy - a.energy);
  };

  const getPointsByCategoryData = () => {
    return energyStats.map((s) => {
      const cat = energyCategories.find((c) => c.key === s.category);
      return {
        name: cat?.label ?? s.category,
        icon: cat?.icon ?? '',
        energy: s.totalEnergy,
        color: cat?.color ?? '#999',
      };
    }).filter((d) => d.energy > 0).sort((a, b) => b.energy - a.energy);
  };

  const getTotalSuns = (): number => {
    return energyStats.reduce((sum, s) => {
      const cfg = getConfigForCategory(energyConfig, s.category);
      if (!cfg) return sum;
      const stars = getTotalStarsEarned(s.totalEnergy, cfg.initialThreshold, cfg.growthRate);
      return sum + getCelestialCounts(stars).suns;
    }, 0);
  };

  const getTotalStars = (): number => {
    return energyStats.reduce((sum, s) => {
      const cfg = getConfigForCategory(energyConfig, s.category);
      if (!cfg) return sum;
      const stars = getTotalStarsEarned(s.totalEnergy, cfg.initialThreshold, cfg.growthRate);
      return sum + getCelestialCounts(stars).stars;
    }, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const chartData = getChartData();

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: 1,
              display: 'flex'
            }}>
              <ShowChartIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                Points Statistics
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Total: <strong>{getTotalPoints()}</strong> points
              </Typography>
            </Box>
          </Box>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 1.5,
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.7)',
                border: 'none',
                '&.Mui-selected': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                  color: 'white',
                },
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)',
                }
              }
            }}
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="weekly">Weekly</ToggleButton>
            <ToggleButton value="monthly">Monthly</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Card>

      {/* Chart */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          {chartData.every(item => item.points === 0) ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ShowChartIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No data yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete some targets to see your progress!
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                    formatter={(value) => [`${value} points`, 'Points']}
                    labelFormatter={(label) => `Period: ${label}`}
                  />
                  <Bar dataKey="points" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getBarColor(entry, index)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Energy Statistics */}
      <Card sx={{ mt: 3, borderRadius: 3 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            borderRadius: 2,
            p: 1,
            display: 'flex'
          }}>
            <WbSunnyIcon sx={{ color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
              Energy Statistics
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              {getTotalSuns()} sun{getTotalSuns() !== 1 ? 's' : ''}, {getTotalStars()} star{getTotalStars() !== 1 ? 's' : ''} active
            </Typography>
          </Box>
        </Box>
      </Card>

      {energyLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : energyStats.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {/* Top row: Energy per category + Distribution */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Energy per category - Horizontal Bar Chart */}
            <Card sx={{ flex: '1 1 320px', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                  Energy per Category
                </Typography>
                <Box sx={{ width: '100%', height: Math.max(200, getEnergyChartData().length * 40) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={getEnergyChartData()}
                      margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                      barSize={20}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#334155' }} tickLine={false} axisLine={false} width={70} />
                      <Tooltip
                        cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        formatter={(value) => [`${value} energy`, 'Energy']}
                        labelFormatter={(label) => label}
                      />
                      <Bar dataKey="energy" radius={[0, 4, 4, 0]}>
                        {getEnergyChartData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>

            {/* Energy distribution - Donut Chart */}
            <Card sx={{ flex: '1 1 280px', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                  Energy by Category
                </Typography>
                <Box sx={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getPointsByCategoryData()}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="energy"
                        strokeWidth={0}
                      >
                        {getPointsByCategoryData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        formatter={(value, name, props: any) => [`${value} energy`, props?.payload?.name ?? '']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                {/* Legend */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
                  {getPointsByCategoryData().map((entry) => (
                    <Chip
                      key={entry.name}
                      label={`${entry.icon} ${entry.name}`}
                      size="small"
                      sx={{
                        bgcolor: entry.color + '15',
                        color: entry.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        border: `1px solid ${entry.color}30`
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Stars progress */}
          <Card sx={{ mt: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                Progress to Next Star
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {energyStats.map((stat) => {
                  const cfg = getConfigForCategory(energyConfig, stat.category);
                  if (!cfg) return null;
                  const cat = energyCategories.find((c) => c.key === stat.category);
                  const label = cat?.label ?? stat.category;
                  const icon = cat?.icon ?? '';
                  const color = cat?.color ?? '#999';

                  const totalStars = getTotalStarsEarned(stat.totalEnergy, cfg.initialThreshold, cfg.growthRate);
                  let remaining = stat.totalEnergy;
                  for (let i = 0; i < totalStars; i++) {
                    remaining -= cfg.initialThreshold * Math.pow(cfg.growthRate, i);
                  }
                  const nextThreshold = cfg.initialThreshold * Math.pow(cfg.growthRate, totalStars);
                  const progress = nextThreshold > 0 ? remaining / nextThreshold : 0;

                  const counts = getCelestialCounts(totalStars);

                  return (
                    <Box key={stat.category}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {icon} {label}
                          {counts.suns > 0 && <span style={{ color: '#f59e0b', marginLeft: 8 }}>☀×{counts.suns}</span>}
                          {counts.moons > 0 && <span style={{ color: '#c0c0c0', marginLeft: 4 }}>☾×{counts.moons}</span>}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.floor(remaining)} / {Math.floor(nextThreshold)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress * 100}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          bgcolor: 'grey.100',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: color,
                            borderRadius: 5,
                          }
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : (
        /* No energy yet */
        <Card sx={{ mt: 2, borderRadius: 3, p: 5, textAlign: 'center' }}>
          <WbSunnyIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Complete targets with categories to start earning energy!
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default StatisticsPage;
